import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { validateUserForEventRegistration, registerUserForEvent, updatePaymentStatus } from './firestoreService';
import { useNavigate } from 'react-router-dom';
import eventsData from './data/events.json';
import eventForms from './data/event-forms.json';
import eventDetails from './data/event-details.json';
import './EventsPage.css';

const EventsPage = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('technical');
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPrizePool = (eventTitle, category) => {
    // First check if the event exists in eventDetails JSON
    if (eventDetails[category]?.[eventTitle]?.prizePool) {
      return eventDetails[category][eventTitle].prizePool;
    }
    
    // Fallback to hardcoded values for events not in JSON
    const prizeMap = {
      'technical': '₹2,500',
      'Recall Rumble': '₹900',
      'Sherlock Holmes': '₹900',
      'Treasure Hunt': '₹2,200',
      'Audio Arena': '₹800',
      'Poster Design': '₹1,200',
      'Typing Thunder': '₹700',
      'Selling Showdown': '₹900',
      'Marvel/DC Quiz': '₹600',
      'Attack On Anime': '₹800',
      'Movie Quiz': '₹1,000'
    };

    if (category === 'technical') return prizeMap['technical'];
    return prizeMap[eventTitle] || '';
  };

  const getEvents = () => {
    const events = eventsData[activeCategory] || eventsData.technical;
    
    if (activeCategory === 'workshops') {
      // Sort workshops by date and time
      return [...events].sort((a, b) => {
        const aDetails = eventDetails[activeCategory]?.[a.title];
        const bDetails = eventDetails[activeCategory]?.[b.title];
        
        if (!aDetails || !bDetails) return 0;
        
        // Parse date (Jan 30 vs Jan 31)
        const aDate = aDetails.date.includes('30') ? 30 : 31;
        const bDate = bDetails.date.includes('30') ? 30 : 31;
        
        if (aDate !== bDate) return aDate - bDate;
        
        // Parse time (10:00 AM vs 2:00 PM)
        const aTime = aDetails.time.includes('10:00') ? 10 : 14;
        const bTime = bDetails.time.includes('10:00') ? 10 : 14;
        
        return aTime - bTime;
      });
    }
    
    return [...events].sort((a, b) => a.title.localeCompare(b.title));
  };

  const getImagePath = (title) => {
    // Check if it's a workshop and has an image in event details
    if (activeCategory === 'workshops' && eventDetails[activeCategory]?.[title]?.image) {
      return eventDetails[activeCategory][title].image;
    }
    
    const imageMap = {
      'Prompt Engineering': 'event.png',
      'Vibe Coding': 'VIBE_CODING.png',
      'Code In Chaos': 'event.png',
      'UI Glow-Up': 'UIGLOWUP.png',
      'Reverse Coding': 'REVERSECODING.png',
      'CP Royale': 'CPROYALE.png',
      '0xDebug': '0xDebug.png',
      'Technical Puzzle clash': 'Technical Puzzle Clash.png',
      'Cyber Ops - Capture The Flag': 'Cyber Ops – Capture The Flag.png',
      'Pixel Decode': 'Pixel Decode.png',
      'Movie Quiz': 'MOVIE QUIZ.png',
      'Poster Design': 'POSTER DESIGN.png',
      'Treasure Hunt': 'TREASURE HUNT.png',
      'Typing Thunder': 'TYPING THUNDER.png',
      'Attack On Anime': 'ATTACK ON ANIME.png',
      'Marvel/DC Quiz': 'MARVEL  DC QUIZ.png',
      'Recall Rumble': 'RECALL RUMBLE.png',
      'Audio Arena': 'AUDIO ARENA.png',
      'Selling Showdown': 'SELLINGSHOWDOWN.png',
      'Sherlock Holmes': 'SHERLOCK HOLMES.png',
      'IPL Auction': 'IPL AUCTION.png',
      'Escape Room': 'ESCAPEROOM.png',
      'Chess': 'CHESS.png',
      'Cryptera Coding Contest': 'Cryptera Coding Contest.png',
      'Project Expo': 'event.png',
      'Paper Presentation': 'Paper Presentation.png',
      'PC Building': 'PC Building.png',
      'BGMI': 'BGMI.png',
      'Free Fire': 'FREEFIRE.png',
      'Music Mania': 'MUSIC_MANIA.png',
      'Artistic Auras': 'ARTISTIC_AURAS.png',
      'Rhyme and Rhythm': 'RHYME_AND_RHYTHM.png',
      'Cinematic Snippets': 'CINEMATIC_SNIPPETS.png',
      'Shutterbug Showcase': 'SHUTTERBUG_SHOWCASE.png'
    };
    return `/EVENTS/${imageMap[title] || 'event.png'}`;
  };

  const handleGeneralRegistration = () => {
    if (!user) {
      alert('Please login to register for events');
      return;
    }

    if (!profile?.profileComplete) {
      alert('Please complete your profile before registering for events.');
      navigate('/account');
      return;
    }

    window.open(eventForms.generalRegistration, '_blank');
  };

  const handleTechOnlyRegistration = () => {
    if (!user) {
      alert('Please login to register for events');
      return;
    }

    if (!profile?.profileComplete) {
      alert('Please complete your profile before registering for events.');
      navigate('/account');
      return;
    }

    window.open(eventForms.techOnly, '_blank');
  };

  const handleEventRegister = (eventTitle) => {
    if (!user) {
      alert('Please login to register for events');
      return;
    }

    if (!profile?.profileComplete) {
      alert('Please complete your profile before registering for events.');
      navigate('/account');
      return;
    }

    // Special handling for Hackathon
    if (eventTitle === 'Hackathon') {
      // Open the hackathon registration form from JSON
      const hackathonForm = eventForms.flagship?.['Hackathon'] || 'https://docs.google.com/forms/d/e/1FAIpQLSfYqtHO6js0sEKFmYpnoRDlfgRv4p_Ju8GPM6F9dxKcKWVwcQ/viewform';
      window.open(hackathonForm, '_blank');
      return;
    }

    let formUrl;
    if (activeCategory === 'online') {
      formUrl = eventForms.online;
    } else if (activeCategory === 'workshops') {
      formUrl = eventForms.workshops.all;
    } else {
      formUrl = eventForms[activeCategory]?.[eventTitle];
    }
    
    if (!formUrl) {
      alert('Registration form not available yet for this event.');
      return;
    }

    window.open(formUrl, '_blank');
  };

  const openModal = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  return (
    <section className="events">

      <div className="container">
        <br />
        <h2 className="section-title">Events</h2>
        
        {(activeCategory === 'technical' || activeCategory === 'non-technical') && (
          <div className="top-registration">
            <h3 className="registration-title">Choose Your Battle Pass</h3>
            <div className="reg-buttons-wrapper">
              <div className="reg-pack">
                <h4 className="pack-title">🔥 Ultimate Pass</h4>
                <p className="pack-price">₹299</p>
                <p className="pack-desc">Unlock All Technical + Non-Technical Events</p>
                <button 
                  className="btn primary top-reg-btn" 
                  onClick={handleGeneralRegistration}
                >
                  On-Spot Registration
                </button>
              </div>
              <div className="reg-pack">
                <h4 className="pack-title">⚡ Tech Elite Pass</h4>
                <p className="pack-price">₹279</p>
                <p className="pack-desc">Access All Technical Events</p>
                <button 
                  className="btn primary top-reg-btn" 
                  onClick={handleTechOnlyRegistration}
                >
                  On-Spot Registration
                </button>
              </div>
            </div>
            <p className="registration-note">
              <i className="fas fa-info-circle"></i>
              Register for on-spot passes using the forms above. Flagship events are also available for online registration.
            </p>
          </div>
        )}

        <div className="event-categories">
          <div className="category-tabs">

            <button
              className={`tab-btn ${activeCategory === 'technical' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('technical')}
            >
              Technical Events
            </button>

            <button
              className={`tab-btn ${activeCategory === 'flagship' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('flagship')}
            >
              Flagship Events
            </button>

            <button
              className={`tab-btn ${activeCategory === 'workshops' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('workshops')}
            >
              Workshops
            </button>

            <button
              className={`tab-btn ${activeCategory === 'non-technical' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('non-technical')}
            >
              Non-Technical Events
            </button>

            <button
              className={`tab-btn ${activeCategory === 'online' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('online')}
            >
              Online Events
            </button>
          </div>

          <div className="event-grid">
            {activeCategory === 'workshops' ? (
              getEvents().map((event, index) => (
                <div key={index} className={`event-card workshop-card ${event.status === 1 ? 'completed' : ''}`}>
                  <div className="event-image-wrapper">
                    <img 
                      src={getImagePath(event.title)} 
                      alt={event.title} 
                      className="event-img"
                      onError={(e) => { e.target.src = '/EVENTS/event.png'; }}
                    />
                    <div className="event-overlay">
                      <span className="event-category">
                        Workshop
                      </span>
                    </div>
                  </div>
                  <div className="event-content">
                    <div className="event-header">
                      <span className="material-icons-outlined event-icon">school</span>
                      <h3 className="event-title">{event.title}</h3>
                    </div>
                    <p className="event-description">{eventDetails[activeCategory]?.[event.title]?.oneLiner || event.desc}</p>
                    {event.speaker && (
                      <div className="workshop-speaker">
                        <span className="material-icons-outlined">person</span>
                        <span>{event.speaker}</span>
                      </div>
                    )}
                    <div className="event-footer">
                      <button 
                        className={`event-register-btn ${event.status === 1 ? 'disabled' : ''}`}
                        onClick={() => event.status === 0 ? handleEventRegister(event.title) : null}
                        disabled={event.status === 1}
                      >
                        {event.status === 1 ? 'Registration Closed' : 'Register'}
                      </button>
                      <button className="view-details-btn" onClick={() => openModal(event)}>
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              getEvents().map((event, index) => (
                <div key={index} className={`event-card ${event.status === 1 ? 'completed' : ''}`}>
                  <div className="event-image-wrapper">
                    <img 
                      src={getImagePath(event.title)} 
                      alt={event.title} 
                      className="event-img"
                      onError={(e) => { e.target.src = '/EVENTS/event.png'; }}
                    />
                    <div className="event-overlay">
                      <span className="event-category">
                        Prize Pool: {getPrizePool(event.title, activeCategory)}
                      </span>
                    </div>
                  </div>
                  <div className="event-content">
                    <div className="event-header">
                      <span className="material-icons-outlined event-icon">event</span>
                      <h3 className="event-title">{event.title}</h3>
                    </div>
                    <p className="event-description">{eventDetails[activeCategory]?.[event.title]?.oneLiner || event.desc}</p>
                    {activeCategory === 'flagship' && eventDetails[activeCategory]?.[event.title]?.entryFee && (
                      <div className="event-entry-fee">
                        <span className="material-icons-outlined">payments</span>
                        <span>Entry Fee: {eventDetails[activeCategory][event.title].entryFee}</span>
                      </div>
                    )}
                    <div className="event-footer">
                      {(activeCategory === 'flagship' || activeCategory === 'online') && (
                        <button 
                          className={`event-register-btn ${event.status === 1 ? 'disabled' : ''}`}
                          onClick={() => event.status === 0 ? handleEventRegister(event.title) : null}
                          disabled={event.status === 1}
                        >
                          {event.status === 1 ? 'Registration Closed' : 'Register'}
                        </button>
                      )}
                      <button className="view-details-btn" onClick={() => openModal(event)}>
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {selectedEvent && (
        <div className="event-modal-overlay" onClick={closeModal}>
          <div className="event-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <span className="material-icons-outlined">close</span>
            </button>
            <div className="event-image-wrapper">
              <img 
                src={getImagePath(selectedEvent.title)} 
                alt={selectedEvent.title}
                className="event-img"
                onError={(e) => { e.target.src = '/EVENTS/event.png'; }}
              />
              <div className="event-overlay">
                {activeCategory === 'workshops' ? (
                  <span className="event-category">Workshop</span>
                ) : (
                  <span className="event-category">
                    Prize Pool: {getPrizePool(selectedEvent.title, activeCategory)}
                  </span>
                )}
              </div>
            </div>
            <div className="event-content">
              <div className="event-header">
                <span className="material-icons-outlined event-icon">event</span>
                <h3 className="event-title">{selectedEvent.title}</h3>
              </div>
              
              <span className="event-type-badge">
                {activeCategory === 'technical' && '💻 Technical'}
                {activeCategory === 'non-technical' && '🎮 Non-Technical'}
                {activeCategory === 'flagship' && '🏆 Flagship'}
                {activeCategory === 'workshops' && '🎓 Workshop'}
                {activeCategory === 'online' && '🌐 Online'}
              </span>
              
              {eventDetails[activeCategory]?.[selectedEvent.title] && (
                <>
                  {activeCategory !== 'workshops' && (
                    <div className="modal-team-size">
                      <span className="material-icons-outlined">group</span>
                      <span>Team Size: {eventDetails[activeCategory][selectedEvent.title].teamSize}</span>
                    </div>
                  )}
                  {activeCategory === 'flagship' && eventDetails[activeCategory][selectedEvent.title].entryFee && (
                    <div className="modal-entry-fee">
                      <span className="material-icons-outlined">payments</span>
                      <span>Entry Fee: {eventDetails[activeCategory][selectedEvent.title].entryFee}</span>
                    </div>
                  )}
                  <p className="event-description">{eventDetails[activeCategory][selectedEvent.title].oneLiner}</p>
                  <p className="modal-description">{eventDetails[activeCategory][selectedEvent.title].description}</p>
                </>
              )}
              
              {!eventDetails[activeCategory]?.[selectedEvent.title] && (
                <p className="event-description">{selectedEvent.desc}</p>
              )}
              
              <div className="modal-time">
                <span className="material-icons-outlined">schedule</span>
                <span>{selectedEvent.duration}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default EventsPage;